import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";

// Logo URLs
const NOIDA_AIRPORT_LOGO = "https://customer-assets.emergentagent.com/job_whatsapp-airport-hub/artifacts/hi3l5nrj_WhatsApp%20Image%202026-04-06%20at%2013.24.30.jpeg";
const FUNDLE_LOGO = "https://customer-assets.emergentagent.com/job_whatsapp-airport-hub/artifacts/oaaehhv6_WhatsApp%20Image%202026-04-06%20at%2013.25.05.jpeg";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success("Login successful");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 lg:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          {/* Logo/Brand */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-2">
              <img 
                src={NOIDA_AIRPORT_LOGO} 
                alt="Noida Airport" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-4">
              Airport Services Portal
            </p>
          </div>

          {/* Login Form */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-zinc-950">
                Sign in
              </h1>
              <p className="text-sm text-zinc-500 mt-2">
                Enter your credentials to access the dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium text-zinc-700 uppercase tracking-wider">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" aria-hidden="true" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@tfs-noida.com"
                    className="pl-10 h-11 rounded-sm border-zinc-200 focus:border-[#0A192F] focus:ring-[#0A192F]"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-medium text-zinc-700 uppercase tracking-wider">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" aria-hidden="true" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 h-11 rounded-sm border-zinc-200 focus:border-[#0A192F] focus:ring-[#0A192F]"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 rounded-sm bg-[#0A192F] hover:bg-zinc-800 text-white font-medium transition-colors duration-150"
                data-testid="login-submit-button"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
            </form>

            <div className="text-center pt-6 space-y-3">
              <div className="flex items-center justify-center gap-2">
                <span className="text-xs text-zinc-400">Powered by</span>
                <img 
                  src={FUNDLE_LOGO} 
                  alt="Fundle" 
                  className="h-6 w-auto object-contain"
                />
              </div>
              <p className="text-xs text-zinc-400">
                © 2025 Noida International Airport
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/1597981/pexels-photo-1597981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="absolute inset-0 bg-[#0A192F]/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-2xl font-bold tracking-tight mb-3">
            Noida International Airport
          </h2>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-md">
            Premium passenger services portal. Manage bookings, QR codes, and deliver exceptional travel experiences.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
